export type ApplicationOntologyTrackId =
  | "application-ontology"
  | "render"
  | "integration"
  | "production";

export interface ApplicationOntologyTrackVerdict {
  trackId: ApplicationOntologyTrackId;
  status: "ready" | "partial" | "deferred" | "blocked";
  label:
    | "ontology-complete"
    | "render-ready"
    | "integration-ready"
    | "production-ready"
    | "partial"
    | "binding-deferred"
    | "blocked";
  blockerDimensionIds: string[];
  deferredDimensionIds: string[];
  authority: "dimension-state-contract";
}

export interface ApplicationOntologyGap {
  id: string;
  dimensionId: string;
  category: string;
  responsibility: "cogo-owned" | "shared-interface" | "external-owned";
  authorityState: string;
  applicability: "required" | "optional" | "not-applicable";
  resolutionState: string;
  blockedTracks: ApplicationOntologyTrackId[];
  evidenceRefs: string[];
  question: string;
  priority: number;
}

export interface ApplicationOntologyGapDecision {
  schemaVersion: "cogo.application-ontology-gap-decision.v2";
  decisionId: string;
  previousAssessmentRevision: string;
  gapId: string;
  authorityState: string;
  applicability?: "required" | "optional" | "not-applicable";
  evidenceRefs: string[];
  actorRef: string;
  decidedAt: string;
  rationale: string;
}

export interface ApplicationOntologyRadarProjectionV2 {
  schemaVersion: "cogo.ontology-sufficiency-radar.v2";
  generatedAt: string;
  assessmentId: string;
  targetTrack: ApplicationOntologyTrackId;
  overview: {
    chartType: "radar";
    aggregation: "minimum-required-dimension-score";
    scoreScale: { minimum: 0; maximum: 100 };
    series: Array<{
      id: "current-evidence" | "target-envelope";
      values: number[];
    }>;
    axes: Array<{
      id: string;
      label: string;
      dimensionIds: string[];
      currentScore: number;
      targetScore: number;
      verdict: "sufficient" | "insufficient" | "not-required";
      blockerDimensionIds: string[];
    }>;
  };
  drillDown: Array<{
    dimensionId: string;
    responsibility: "cogo-owned" | "shared-interface" | "external-owned";
    authorityState: string;
    applicability: "required" | "optional" | "not-applicable";
    resolutionState: string;
    score: number;
    verdict: string;
    requiredForTarget: boolean;
    acceptedForTarget: boolean;
    evidenceRefs: string[];
    gapId: string | null;
  }>;
  authority: {
    projectionOnly: true;
    scoreIsAuthority: false;
    promotionVerdictSource: "readiness-track-dimension-state-contract";
  };
}

interface ApplicationOntologyEvidenceItem {
  id: string;
  authorityState: string;
  evidenceRefs: string[];
  [key: string]: unknown;
}

interface ApplicationOntologyPhysicalBinding {
  id: string;
  logicalRequirementRef: string;
  status: "unbound" | "candidate" | "contracted" | "verified";
  evidenceRefs: string[];
  [key: string]: unknown;
}

export interface ApplicationOntologyAssessmentV2 {
  schemaVersion: "cogo.application-ontology-assessment.v2";
  assessmentRevision: string;
  previousAssessmentRevision: string | null;
  semanticCoreRevision: string;
  applicationOntologyRevision: string;
  integrationRevision: string;
  assessmentId: string;
  sourceFactBundleId: string;
  sourceProfileId: string;
  primaryArchetype: string;
  systemCompositionProfiles: string[];
  activeSignals: string[];
  semanticFields: ApplicationOntologyEvidenceItem[];
  logicalDataRequirements: ApplicationOntologyEvidenceItem[];
  physicalBindings: ApplicationOntologyPhysicalBinding[];
  localeProfile: Record<string, unknown> | null;
  jurisdictionProfiles: ApplicationOntologyEvidenceItem[];
  dimensionStates: Record<string, string>;
  dimensionApplicability: Record<
    string,
    "required" | "optional" | "not-applicable"
  >;
  dimensionEvidenceRefs: Record<string, string[]>;
  verdicts: Record<ApplicationOntologyTrackId, ApplicationOntologyTrackVerdict>;
  gaps: ApplicationOntologyGap[];
  displayQuestions: ApplicationOntologyGap[];
  decisionHistory: ApplicationOntologyGapDecision[];
  radarProjection: ApplicationOntologyRadarProjectionV2;
  authority: {
    scoreIsAuthority: false;
    sourceAdapterIsClassifier: false;
    physicalBindingChangesSemanticCore: false;
    modelFinalWriter: false;
  };
}

export interface ApplicationOntologyBuildabilityResultV1 {
  schemaVersion: "cogo.application-ontology-buildability-result.v1";
  assessmentId: string;
  assessmentRevision: string;
  status: "rejected" | "candidate-buildable" | "admitted";
  candidateABoxBuildable: boolean;
  applicationOntologyAdmissionAllowed: boolean;
  renderProjectionAllowed: boolean;
  integrationProjectionAllowed: boolean;
  productionProjectionAllowed: boolean;
  generationCeiling:
    | "none"
    | "ontology-candidate"
    | "ontology-admitted"
    | "render-candidate"
    | "integration-candidate"
    | "production-candidate";
  candidateABox: Record<string, unknown> | null;
  checks: Array<{
    id: string;
    status: "pass" | "fail";
    blocksCandidate: boolean;
    observed: string;
    expected: string;
  }>;
  blockingCheckIds: string[];
  unresolvedGapIds: string[];
  clarificationQuestionIds: string[];
  claimBoundary: string;
}

export const APPLICATION_ONTOLOGY_V2_DIMENSION_IDS = [
  "product-scope",
  "ui-structure",
  "visual-design",
  "client-state",
  "client-actionflow",
  "semantic-value-model",
  "logical-data-contract",
  "physical-datasource-binding",
  "locale-language",
  "jurisdiction-policy",
  "external-business-rules",
  "auth-security",
  "error-recovery",
  "terminal-outcomes",
  "accessibility",
  "nonfunctional",
  "observability-audit",
  "deployment-operations",
] as const;

export const APPLICATION_ONTOLOGY_V2_TRACK_IDS = [
  "application-ontology",
  "render",
  "integration",
  "production",
] as const satisfies readonly ApplicationOntologyTrackId[];

export const APPLICATION_ONTOLOGY_V2_RADAR_AXES = [
  {
    id: "scope-authority",
    label: "Scope",
    dimensionIds: ["product-scope"],
  },
  {
    id: "ui-design",
    label: "UI and design",
    dimensionIds: ["ui-structure", "visual-design", "locale-language"],
  },
  {
    id: "client-behavior",
    label: "Client behavior",
    dimensionIds: ["client-state", "client-actionflow"],
  },
  {
    id: "semantic-data",
    label: "Semantic data",
    dimensionIds: [
      "semantic-value-model",
      "logical-data-contract",
      "physical-datasource-binding",
      "jurisdiction-policy",
    ],
  },
  {
    id: "external-trust",
    label: "External trust",
    dimensionIds: [
      "external-business-rules",
      "auth-security",
      "error-recovery",
      "terminal-outcomes",
    ],
  },
  {
    id: "quality-operations",
    label: "Quality and operations",
    dimensionIds: [
      "accessibility",
      "nonfunctional",
      "observability-audit",
      "deployment-operations",
    ],
  },
] as const;

export type ApplicationOntologyV2DimensionId =
  (typeof APPLICATION_ONTOLOGY_V2_DIMENSION_IDS)[number];

export interface StudioApplicationOntologyV2Binding {
  schemaVersion: "cogo.studio-application-ontology-v2-binding.v1";
  releaseId: string;
  projectionHash: string;
  assessment: ApplicationOntologyAssessmentV2;
  buildability: ApplicationOntologyBuildabilityResultV1;
}

export interface StudioApplicationOntologyV2ProjectionPayload {
  schemaVersion: "cogo.studio-application-ontology-v2-projection.v1";
  releaseId: string;
  assessment: ApplicationOntologyAssessmentV2;
  buildability: ApplicationOntologyBuildabilityResultV1;
}

export interface StudioApplicationOntologyV2View {
  schemaVersion: "cogo.studio-application-ontology-v2-view.v1";
  projectId: string;
  subject: {
    schemaVersion: "cogo.ontology-subject-ref.v1";
    kind: "application-abox";
    subjectId: string;
  };
  releaseId: string;
  projectionHash: string;
  assessment: ApplicationOntologyAssessmentV2;
  buildability: ApplicationOntologyBuildabilityResultV1;
  authority: {
    releaseBound: true;
    scoreIsAuthority: false;
    browserMayRecomputeVerdicts: false;
  };
}

export interface StudioApplicationOntologyV2Gate {
  schemaVersion: "studio-application-ontology-v2-gate.v1";
  status: "ready" | "candidate" | "blocked" | "not-required";
  required: boolean;
  releaseId: string | null;
  assessmentRevision: string | null;
  semanticCoreRevision: string | null;
  applicationOntologyRevision: string | null;
  integrationRevision: string | null;
  buildabilityStatus: ApplicationOntologyBuildabilityResultV1["status"] | null;
  clientGenerationAllowed: boolean;
  publishAllowed: boolean;
  verdicts: Partial<
    Record<ApplicationOntologyTrackId, ApplicationOntologyTrackVerdict>
  >;
  blockerDimensionIds: string[];
  clarificationQuestionIds: string[];
  reasons: string[];
}

export interface StudioApplicationOntologyGapDecisionInput {
  gapId: string;
  authorityState:
    | "observed"
    | "contracted"
    | "derived"
    | "candidate"
    | "mock-approved"
    | "external-out-of-scope"
    | "missing"
    | "conflict";
  applicability?: "required" | "optional" | "not-applicable";
  evidenceRefs: string[];
  rationale: string;
  expectedAssessmentRevision: string;
}

const DIMENSION_ID_SET = new Set<string>(APPLICATION_ONTOLOGY_V2_DIMENSION_IDS);
const TRACK_ID_SET = new Set<string>(APPLICATION_ONTOLOGY_V2_TRACK_IDS);
const AUTHORITY_STATE_SET = new Set([
  "observed",
  "contracted",
  "derived",
  "candidate",
  "mock-approved",
  "external-out-of-scope",
  "missing",
  "conflict",
]);
const APPLICABILITY_SET = new Set(["required", "optional", "not-applicable"]);
const RESOLUTION_STATE_SET = new Set([
  "resolved",
  "clarification-required",
  "deferred-binding",
  "external-contract-required",
  "blocked",
  "waived",
]);
const GAP_CATEGORY_SET = new Set([
  "semantic-gap",
  "display-format-gap",
  "state-gap",
  "interaction-gap",
  "logical-data-contract-gap",
  "physical-binding-gap",
  "localization-gap",
  "jurisdiction-policy-gap",
  "external-authority-gap",
  "evidence-provenance-gap",
  "security-privacy-gap",
  "verification-gap",
]);
const TRACK_STATUS_SET = new Set(["ready", "partial", "deferred", "blocked"]);
const TRACK_LABEL_SET = new Set([
  "ontology-complete",
  "render-ready",
  "integration-ready",
  "production-ready",
  "partial",
  "binding-deferred",
  "blocked",
]);
const GENERATION_CEILING_SET = new Set([
  "none",
  "ontology-candidate",
  "ontology-admitted",
  "render-candidate",
  "integration-candidate",
  "production-candidate",
]);
const REVISION_PATTERN = /^sha256:[a-f0-9]{64}$/;

function record(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function nonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function revision(value: unknown): value is string {
  return typeof value === "string" && REVISION_PATTERN.test(value);
}

function stringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every(nonEmptyString);
}

function uniqueStringArray(value: unknown): value is string[] {
  return stringArray(value) && new Set(value).size === value.length;
}

function sameStringSet(left: string[], right: string[]): boolean {
  return (
    left.length === right.length && left.every((value) => right.includes(value))
  );
}

function canonicalValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonicalValue);
  const candidate = record(value);
  if (!candidate) return value;
  return Object.fromEntries(
    Object.keys(candidate)
      .filter((key) => candidate[key] !== undefined)
      .sort()
      .map((key) => [key, canonicalValue(candidate[key])]),
  );
}

export function canonicalApplicationOntologyV2ProjectionJson(input: {
  releaseId: string;
  assessment: ApplicationOntologyAssessmentV2;
  buildability: ApplicationOntologyBuildabilityResultV1;
}): string {
  const payload: StudioApplicationOntologyV2ProjectionPayload = {
    schemaVersion: "cogo.studio-application-ontology-v2-projection.v1",
    releaseId: input.releaseId,
    assessment: input.assessment,
    buildability: input.buildability,
  };
  return JSON.stringify(canonicalValue(payload));
}

function exactKeys(value: Record<string, unknown>, keys: readonly string[]) {
  const actual = Object.keys(value).sort();
  const expected = [...keys].sort();
  return (
    actual.length === expected.length &&
    actual.every((key, index) => key === expected[index])
  );
}

function score(value: unknown): value is number {
  return (
    typeof value === "number" &&
    Number.isFinite(value) &&
    value >= 0 &&
    value <= 100
  );
}

function isEvidenceBearingRecord(value: unknown): boolean {
  const candidate = record(value);
  return Boolean(
    candidate &&
      nonEmptyString(candidate.id) &&
      AUTHORITY_STATE_SET.has(String(candidate.authorityState)) &&
      uniqueStringArray(candidate.evidenceRefs),
  );
}

function isTrackVerdict(
  value: unknown,
  expectedTrackId: ApplicationOntologyTrackId,
): value is ApplicationOntologyTrackVerdict {
  const candidate = record(value);
  return Boolean(
    candidate &&
      candidate.trackId === expectedTrackId &&
      TRACK_STATUS_SET.has(String(candidate.status)) &&
      TRACK_LABEL_SET.has(String(candidate.label)) &&
      uniqueStringArray(candidate.blockerDimensionIds) &&
      candidate.blockerDimensionIds.every((id) => DIMENSION_ID_SET.has(id)) &&
      uniqueStringArray(candidate.deferredDimensionIds) &&
      candidate.deferredDimensionIds.every((id) => DIMENSION_ID_SET.has(id)) &&
      candidate.authority === "dimension-state-contract",
  );
}

function isGap(value: unknown): value is ApplicationOntologyGap {
  const candidate = record(value);
  return Boolean(
    candidate &&
      nonEmptyString(candidate.id) &&
      nonEmptyString(candidate.dimensionId) &&
      DIMENSION_ID_SET.has(candidate.dimensionId) &&
      GAP_CATEGORY_SET.has(String(candidate.category)) &&
      ["cogo-owned", "shared-interface", "external-owned"].includes(
        String(candidate.responsibility),
      ) &&
      AUTHORITY_STATE_SET.has(String(candidate.authorityState)) &&
      APPLICABILITY_SET.has(String(candidate.applicability)) &&
      RESOLUTION_STATE_SET.has(String(candidate.resolutionState)) &&
      uniqueStringArray(candidate.blockedTracks) &&
      candidate.blockedTracks.every((id) => TRACK_ID_SET.has(id)) &&
      uniqueStringArray(candidate.evidenceRefs) &&
      nonEmptyString(candidate.question) &&
      Number.isInteger(candidate.priority) &&
      Number(candidate.priority) >= 0,
  );
}

function isDecision(value: unknown): value is ApplicationOntologyGapDecision {
  const candidate = record(value);
  return Boolean(
    candidate &&
      candidate.schemaVersion === "cogo.application-ontology-gap-decision.v2" &&
      nonEmptyString(candidate.decisionId) &&
      revision(candidate.previousAssessmentRevision) &&
      nonEmptyString(candidate.gapId) &&
      AUTHORITY_STATE_SET.has(String(candidate.authorityState)) &&
      (candidate.applicability === undefined ||
        APPLICABILITY_SET.has(String(candidate.applicability))) &&
      uniqueStringArray(candidate.evidenceRefs) &&
      nonEmptyString(candidate.actorRef) &&
      nonEmptyString(candidate.decidedAt) &&
      !Number.isNaN(Date.parse(candidate.decidedAt)) &&
      nonEmptyString(candidate.rationale),
  );
}

function isRadar(
  value: unknown,
): value is ApplicationOntologyRadarProjectionV2 {
  const candidate = record(value);
  const overview = record(candidate?.overview);
  const authority = record(candidate?.authority);
  if (
    !candidate ||
    candidate.schemaVersion !== "cogo.ontology-sufficiency-radar.v2" ||
    !nonEmptyString(candidate.generatedAt) ||
    Number.isNaN(Date.parse(candidate.generatedAt)) ||
    !nonEmptyString(candidate.assessmentId) ||
    !TRACK_ID_SET.has(String(candidate.targetTrack)) ||
    !overview ||
    overview.chartType !== "radar" ||
    overview.aggregation !== "minimum-required-dimension-score" ||
    !record(overview.scoreScale) ||
    record(overview.scoreScale)?.minimum !== 0 ||
    record(overview.scoreScale)?.maximum !== 100 ||
    !Array.isArray(overview.axes) ||
    overview.axes.length !== APPLICATION_ONTOLOGY_V2_RADAR_AXES.length ||
    !Array.isArray(overview.series) ||
    overview.series.length !== 2 ||
    !Array.isArray(candidate.drillDown) ||
    candidate.drillDown.length !==
      APPLICATION_ONTOLOGY_V2_DIMENSION_IDS.length ||
    authority?.projectionOnly !== true ||
    authority.scoreIsAuthority !== false ||
    authority.promotionVerdictSource !==
      "readiness-track-dimension-state-contract"
  ) {
    return false;
  }

  for (const [
    index,
    expected,
  ] of APPLICATION_ONTOLOGY_V2_RADAR_AXES.entries()) {
    const axis = record(overview.axes[index]);
    if (
      !axis ||
      axis.id !== expected.id ||
      axis.label !== expected.label ||
      !Array.isArray(axis.dimensionIds) ||
      axis.dimensionIds.length !== expected.dimensionIds.length ||
      !axis.dimensionIds.every(
        (id, dimensionIndex) => id === expected.dimensionIds[dimensionIndex],
      ) ||
      !score(axis.currentScore) ||
      !score(axis.targetScore) ||
      !["sufficient", "insufficient", "not-required"].includes(
        String(axis.verdict),
      ) ||
      !uniqueStringArray(axis.blockerDimensionIds) ||
      !axis.blockerDimensionIds.every((id) =>
        (expected.dimensionIds as readonly string[]).includes(id),
      )
    ) {
      return false;
    }
  }

  const current = record(overview.series[0]);
  const target = record(overview.series[1]);
  const axes = overview.axes as Array<Record<string, unknown>>;
  if (
    current?.id !== "current-evidence" ||
    !Array.isArray(current.values) ||
    current.values.length !== axes.length ||
    !current.values.every(
      (value, index) => value === axes[index].currentScore,
    ) ||
    target?.id !== "target-envelope" ||
    !Array.isArray(target.values) ||
    target.values.length !== axes.length ||
    !target.values.every((value, index) => value === axes[index].targetScore)
  ) {
    return false;
  }

  const drillDownIds = new Set<string>();
  for (const item of candidate.drillDown) {
    const dimension = record(item);
    if (
      !dimension ||
      !nonEmptyString(dimension.dimensionId) ||
      !DIMENSION_ID_SET.has(dimension.dimensionId) ||
      drillDownIds.has(dimension.dimensionId) ||
      !["cogo-owned", "shared-interface", "external-owned"].includes(
        String(dimension.responsibility),
      ) ||
      !AUTHORITY_STATE_SET.has(String(dimension.authorityState)) ||
      !APPLICABILITY_SET.has(String(dimension.applicability)) ||
      !RESOLUTION_STATE_SET.has(String(dimension.resolutionState)) ||
      !score(dimension.score) ||
      !nonEmptyString(dimension.verdict) ||
      typeof dimension.requiredForTarget !== "boolean" ||
      typeof dimension.acceptedForTarget !== "boolean" ||
      !uniqueStringArray(dimension.evidenceRefs) ||
      !(dimension.gapId === null || nonEmptyString(dimension.gapId))
    ) {
      return false;
    }
    drillDownIds.add(dimension.dimensionId);
  }
  return drillDownIds.size === APPLICATION_ONTOLOGY_V2_DIMENSION_IDS.length;
}

export function isApplicationOntologyAssessmentV2(
  value: unknown,
): value is ApplicationOntologyAssessmentV2 {
  const assessment = record(value);
  const authority = record(assessment?.authority);
  const dimensionStates = record(assessment?.dimensionStates);
  const dimensionApplicability = record(assessment?.dimensionApplicability);
  const dimensionEvidenceRefs = record(assessment?.dimensionEvidenceRefs);
  const verdicts = record(assessment?.verdicts);
  if (
    !assessment ||
    assessment.schemaVersion !== "cogo.application-ontology-assessment.v2" ||
    !revision(assessment.assessmentRevision) ||
    !(
      assessment.previousAssessmentRevision === null ||
      revision(assessment.previousAssessmentRevision)
    ) ||
    !revision(assessment.semanticCoreRevision) ||
    !revision(assessment.applicationOntologyRevision) ||
    !revision(assessment.integrationRevision) ||
    !nonEmptyString(assessment.assessmentId) ||
    !nonEmptyString(assessment.sourceFactBundleId) ||
    !nonEmptyString(assessment.sourceProfileId) ||
    !nonEmptyString(assessment.primaryArchetype) ||
    !uniqueStringArray(assessment.systemCompositionProfiles) ||
    !uniqueStringArray(assessment.activeSignals) ||
    !Array.isArray(assessment.semanticFields) ||
    assessment.semanticFields.length === 0 ||
    !assessment.semanticFields.every(isEvidenceBearingRecord) ||
    !Array.isArray(assessment.logicalDataRequirements) ||
    !assessment.logicalDataRequirements.every(isEvidenceBearingRecord) ||
    !Array.isArray(assessment.physicalBindings) ||
    !assessment.physicalBindings.every((binding) => {
      const candidate = record(binding);
      return Boolean(
        candidate &&
          nonEmptyString(candidate.id) &&
          nonEmptyString(candidate.logicalRequirementRef) &&
          ["unbound", "candidate", "contracted", "verified"].includes(
            String(candidate.status),
          ) &&
          uniqueStringArray(candidate.evidenceRefs),
      );
    }) ||
    !(
      assessment.localeProfile === null ||
      (() => {
        const locale = record(assessment.localeProfile);
        return Boolean(
          locale &&
            nonEmptyString(locale.languageTag) &&
            ["ltr", "rtl"].includes(String(locale.direction)) &&
            AUTHORITY_STATE_SET.has(String(locale.authorityState)) &&
            uniqueStringArray(locale.evidenceRefs),
        );
      })()
    ) ||
    !Array.isArray(assessment.jurisdictionProfiles) ||
    !assessment.jurisdictionProfiles.every(isEvidenceBearingRecord) ||
    !dimensionStates ||
    !exactKeys(dimensionStates, APPLICATION_ONTOLOGY_V2_DIMENSION_IDS) ||
    !Object.values(dimensionStates).every((state) =>
      AUTHORITY_STATE_SET.has(String(state)),
    ) ||
    !dimensionApplicability ||
    !exactKeys(dimensionApplicability, APPLICATION_ONTOLOGY_V2_DIMENSION_IDS) ||
    !Object.values(dimensionApplicability).every((state) =>
      APPLICABILITY_SET.has(String(state)),
    ) ||
    !dimensionEvidenceRefs ||
    !exactKeys(dimensionEvidenceRefs, APPLICATION_ONTOLOGY_V2_DIMENSION_IDS) ||
    !Object.values(dimensionEvidenceRefs).every(uniqueStringArray) ||
    !verdicts ||
    !exactKeys(verdicts, APPLICATION_ONTOLOGY_V2_TRACK_IDS) ||
    !APPLICATION_ONTOLOGY_V2_TRACK_IDS.every((trackId) =>
      isTrackVerdict(verdicts[trackId], trackId),
    ) ||
    !Array.isArray(assessment.gaps) ||
    !assessment.gaps.every(isGap) ||
    !Array.isArray(assessment.displayQuestions) ||
    assessment.displayQuestions.length > 3 ||
    !assessment.displayQuestions.every(isGap) ||
    !Array.isArray(assessment.decisionHistory) ||
    !assessment.decisionHistory.every(isDecision) ||
    !isRadar(assessment.radarProjection) ||
    authority?.scoreIsAuthority !== false ||
    authority.sourceAdapterIsClassifier !== false ||
    authority.physicalBindingChangesSemanticCore !== false ||
    authority.modelFinalWriter !== false
  ) {
    return false;
  }

  const gapIds = new Set(
    (assessment.gaps as ApplicationOntologyGap[]).map((gap) => gap.id),
  );
  const questionIds = new Set(
    (assessment.displayQuestions as ApplicationOntologyGap[]).map(
      (question) => question.id,
    ),
  );
  const radar =
    assessment.radarProjection as ApplicationOntologyRadarProjectionV2;
  return (
    radar.assessmentId === assessment.assessmentId &&
    questionIds.size === assessment.displayQuestions.length &&
    [...questionIds].every((id) => gapIds.has(id)) &&
    radar.drillDown.every(
      (dimension) =>
        dimensionStates[dimension.dimensionId] === dimension.authorityState &&
        dimensionApplicability[dimension.dimensionId] ===
          dimension.applicability &&
        JSON.stringify(dimensionEvidenceRefs[dimension.dimensionId]) ===
          JSON.stringify(dimension.evidenceRefs) &&
        (dimension.gapId === null || gapIds.has(dimension.gapId)),
    )
  );
}

export function isApplicationOntologyBuildabilityResultV1(
  value: unknown,
  assessment?: ApplicationOntologyAssessmentV2,
): value is ApplicationOntologyBuildabilityResultV1 {
  const result = record(value);
  if (
    !result ||
    result.schemaVersion !==
      "cogo.application-ontology-buildability-result.v1" ||
    !nonEmptyString(result.assessmentId) ||
    !revision(result.assessmentRevision) ||
    !["rejected", "candidate-buildable", "admitted"].includes(
      String(result.status),
    ) ||
    typeof result.candidateABoxBuildable !== "boolean" ||
    typeof result.applicationOntologyAdmissionAllowed !== "boolean" ||
    typeof result.renderProjectionAllowed !== "boolean" ||
    typeof result.integrationProjectionAllowed !== "boolean" ||
    typeof result.productionProjectionAllowed !== "boolean" ||
    !GENERATION_CEILING_SET.has(String(result.generationCeiling)) ||
    !Array.isArray(result.checks) ||
    result.checks.length === 0 ||
    !result.checks.every((check) => {
      const candidate = record(check);
      return Boolean(
        candidate &&
          nonEmptyString(candidate.id) &&
          ["pass", "fail"].includes(String(candidate.status)) &&
          typeof candidate.blocksCandidate === "boolean" &&
          typeof candidate.observed === "string" &&
          nonEmptyString(candidate.expected),
      );
    }) ||
    !uniqueStringArray(result.blockingCheckIds) ||
    !uniqueStringArray(result.unresolvedGapIds) ||
    !uniqueStringArray(result.clarificationQuestionIds) ||
    !nonEmptyString(result.claimBoundary)
  ) {
    return false;
  }

  const checks =
    result.checks as ApplicationOntologyBuildabilityResultV1["checks"];
  const blockingCheckIds = result.blockingCheckIds as string[];
  const expectedBlockingCheckIds = checks
    .filter((check) => check.status === "fail" && check.blocksCandidate)
    .map((check) => check.id);
  if (
    new Set(checks.map((check) => check.id)).size !== checks.length ||
    !sameStringSet(blockingCheckIds, expectedBlockingCheckIds) ||
    result.candidateABoxBuildable !== (blockingCheckIds.length === 0)
  ) {
    return false;
  }

  const candidate = record(result.candidateABox);
  if (result.candidateABoxBuildable) {
    const candidateAuthority = record(candidate?.authority);
    if (
      !candidate ||
      candidate.schemaVersion !==
        "cogo.application-ontology-candidate-abox.v1" ||
      !revision(candidate.aboxRevision) ||
      !nonEmptyString(candidate.subjectId) ||
      candidateAuthority?.candidateProjection !== true ||
      candidateAuthority.physicalBindingsExcluded !== true ||
      candidateAuthority.scoreIsAuthority !== false ||
      candidateAuthority.modelFinalWriter !== false ||
      candidateAuthority.activeAdmissionRequiresApplicationVerdict !== true
    ) {
      return false;
    }
  } else if (result.candidateABox !== null) {
    return false;
  }

  if (!assessment) return true;
  const appReady =
    assessment.verdicts["application-ontology"].status === "ready";
  const renderReady = assessment.verdicts.render.status === "ready";
  const integrationReady = assessment.verdicts.integration.status === "ready";
  const productionReady = assessment.verdicts.production.status === "ready";
  const expectedGenerationCeiling = !result.candidateABoxBuildable
    ? "none"
    : result.productionProjectionAllowed
      ? "production-candidate"
      : result.integrationProjectionAllowed
        ? "integration-candidate"
        : result.renderProjectionAllowed
          ? "render-candidate"
          : result.applicationOntologyAdmissionAllowed
            ? "ontology-admitted"
            : "ontology-candidate";
  const expectedGapIds = assessment.gaps.map((gap) => gap.id);
  const expectedQuestionIds = assessment.displayQuestions.map(
    (question) => question.id,
  );
  return (
    result.assessmentId === assessment.assessmentId &&
    result.assessmentRevision === assessment.assessmentRevision &&
    result.applicationOntologyAdmissionAllowed ===
      (result.candidateABoxBuildable && appReady) &&
    result.renderProjectionAllowed ===
      (result.candidateABoxBuildable && renderReady) &&
    result.integrationProjectionAllowed ===
      (result.candidateABoxBuildable && integrationReady) &&
    result.productionProjectionAllowed ===
      (result.candidateABoxBuildable && productionReady) &&
    result.generationCeiling === expectedGenerationCeiling &&
    sameStringSet(result.unresolvedGapIds, expectedGapIds) &&
    sameStringSet(result.clarificationQuestionIds, expectedQuestionIds) &&
    (!candidate ||
      (candidate.subjectId === assessment.assessmentId &&
        candidate.semanticCoreRevision === assessment.semanticCoreRevision &&
        candidate.applicationOntologyRevision ===
          assessment.applicationOntologyRevision)) &&
    result.status ===
      (!result.candidateABoxBuildable
        ? "rejected"
        : result.applicationOntologyAdmissionAllowed
          ? "admitted"
          : "candidate-buildable")
  );
}

export function isStudioApplicationOntologyV2Binding(
  value: unknown,
): value is StudioApplicationOntologyV2Binding {
  const binding = record(value);
  return Boolean(
    binding &&
      binding.schemaVersion ===
        "cogo.studio-application-ontology-v2-binding.v1" &&
      nonEmptyString(binding.releaseId) &&
      revision(binding.projectionHash) &&
      isApplicationOntologyAssessmentV2(binding.assessment) &&
      isApplicationOntologyBuildabilityResultV1(
        binding.buildability,
        binding.assessment as ApplicationOntologyAssessmentV2,
      ),
  );
}

export function isStudioApplicationOntologyV2View(
  value: unknown,
): value is StudioApplicationOntologyV2View {
  const view = record(value);
  const subject = record(view?.subject);
  const authority = record(view?.authority);
  return Boolean(
    view &&
      view.schemaVersion === "cogo.studio-application-ontology-v2-view.v1" &&
      nonEmptyString(view.projectId) &&
      subject?.schemaVersion === "cogo.ontology-subject-ref.v1" &&
      subject.kind === "application-abox" &&
      nonEmptyString(subject.subjectId) &&
      nonEmptyString(view.releaseId) &&
      revision(view.projectionHash) &&
      isApplicationOntologyAssessmentV2(view.assessment) &&
      view.assessment.assessmentId === subject.subjectId &&
      isApplicationOntologyBuildabilityResultV1(
        view.buildability,
        view.assessment as ApplicationOntologyAssessmentV2,
      ) &&
      authority?.releaseBound === true &&
      authority.scoreIsAuthority === false &&
      authority.browserMayRecomputeVerdicts === false,
  );
}

function projectCandidates(project: unknown): unknown[] {
  const root = record(project);
  const manifest = record(root?.manifest);
  const metadata = record(root?.metadata);
  const settings = record(root?.settings);
  const applicationMetadata = record(settings?.cogoApplicationMetadata);
  return [
    applicationMetadata?.applicationOntologyV2,
    metadata?.applicationOntologyV2,
    manifest?.applicationOntologyV2,
    root?.applicationOntologyV2,
  ];
}

export function readProjectApplicationOntologyV2Binding(
  project: unknown,
): StudioApplicationOntologyV2Binding | null {
  return (
    projectCandidates(project).find(isStudioApplicationOntologyV2Binding) ??
    null
  );
}

export function projectClaimsApplicationOntologyV2(project: unknown): boolean {
  const root = record(project);
  const manifest = record(root?.manifest);
  const metadata = record(root?.metadata);
  return (
    projectCandidates(project).some((candidate) => candidate !== undefined) ||
    manifest?.sourceDerivedGeneration === true ||
    metadata?.sourceDerivedGeneration === true ||
    Boolean(
      record(record(root?.settings)?.cogoApplicationMetadata)
        ?.applicationSourceAssessment,
    ) ||
    Boolean(metadata?.applicationSourceAssessment) ||
    Boolean(manifest?.applicationSourceAssessment)
  );
}

export function buildStudioApplicationOntologyV2View(input: {
  projectId: string;
  subjectId: string;
  selectedReleaseId: string;
  binding: StudioApplicationOntologyV2Binding;
}): StudioApplicationOntologyV2View | null {
  if (
    input.binding.releaseId !== input.selectedReleaseId ||
    input.binding.assessment.assessmentId !== input.subjectId
  ) {
    return null;
  }
  return {
    schemaVersion: "cogo.studio-application-ontology-v2-view.v1",
    projectId: input.projectId,
    subject: {
      schemaVersion: "cogo.ontology-subject-ref.v1",
      kind: "application-abox",
      subjectId: input.subjectId,
    },
    releaseId: input.selectedReleaseId,
    projectionHash: input.binding.projectionHash,
    assessment: input.binding.assessment,
    buildability: input.binding.buildability,
    authority: {
      releaseBound: true,
      scoreIsAuthority: false,
      browserMayRecomputeVerdicts: false,
    },
  };
}

export function evaluateApplicationOntologyV2Gate(
  project: unknown,
): StudioApplicationOntologyV2Gate {
  const required = projectClaimsApplicationOntologyV2(project);
  const binding = readProjectApplicationOntologyV2Binding(project);
  if (!binding) {
    return {
      schemaVersion: "studio-application-ontology-v2-gate.v1",
      status: required ? "blocked" : "not-required",
      required,
      releaseId: null,
      assessmentRevision: null,
      semanticCoreRevision: null,
      applicationOntologyRevision: null,
      integrationRevision: null,
      buildabilityStatus: null,
      clientGenerationAllowed: false,
      publishAllowed: false,
      verdicts: {},
      blockerDimensionIds: [],
      clarificationQuestionIds: [],
      reasons: required
        ? [
            "Source-derived applications require a release-bound application ontology v2 assessment and buildability result.",
          ]
        : [],
    };
  }

  const { assessment, buildability } = binding;
  const clientGenerationAllowed =
    buildability.status === "admitted" &&
    buildability.applicationOntologyAdmissionAllowed &&
    buildability.renderProjectionAllowed;
  const publishAllowed =
    clientGenerationAllowed &&
    buildability.integrationProjectionAllowed &&
    buildability.productionProjectionAllowed;
  const blockerDimensionIds = Array.from(
    new Set(
      APPLICATION_ONTOLOGY_V2_TRACK_IDS.flatMap(
        (trackId) => assessment.verdicts[trackId].blockerDimensionIds,
      ),
    ),
  );
  const reasons: string[] = [];
  if (buildability.status === "rejected") {
    reasons.push(
      `Application ontology candidate was rejected by checks: ${buildability.blockingCheckIds.join(", ") || "unknown"}.`,
    );
  } else if (buildability.status === "candidate-buildable") {
    reasons.push(
      "Application ontology is candidate-buildable but has not been admitted.",
    );
  }
  for (const trackId of APPLICATION_ONTOLOGY_V2_TRACK_IDS) {
    const verdict = assessment.verdicts[trackId];
    if (verdict.status !== "ready") {
      reasons.push(
        `${trackId} is ${verdict.status} (${verdict.label}); blockers: ${verdict.blockerDimensionIds.join(", ") || "none"}.`,
      );
    }
  }

  return {
    schemaVersion: "studio-application-ontology-v2-gate.v1",
    status:
      buildability.status === "rejected"
        ? "blocked"
        : publishAllowed
          ? "ready"
          : "candidate",
    required: true,
    releaseId: binding.releaseId,
    assessmentRevision: assessment.assessmentRevision,
    semanticCoreRevision: assessment.semanticCoreRevision,
    applicationOntologyRevision: assessment.applicationOntologyRevision,
    integrationRevision: assessment.integrationRevision,
    buildabilityStatus: buildability.status,
    clientGenerationAllowed,
    publishAllowed,
    verdicts: assessment.verdicts,
    blockerDimensionIds,
    clarificationQuestionIds: buildability.clarificationQuestionIds,
    reasons,
  };
}

export function isStudioApplicationOntologyGapDecisionInput(
  value: unknown,
): value is StudioApplicationOntologyGapDecisionInput {
  const input = record(value);
  return Boolean(
    input &&
      nonEmptyString(input.gapId) &&
      AUTHORITY_STATE_SET.has(String(input.authorityState)) &&
      (input.applicability === undefined ||
        APPLICABILITY_SET.has(String(input.applicability))) &&
      uniqueStringArray(input.evidenceRefs) &&
      nonEmptyString(input.rationale) &&
      input.rationale.length <= 2_048 &&
      revision(input.expectedAssessmentRevision),
  );
}
